import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Send, ChevronRight, CheckCircle2 } from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "@/data/services";
import { useSiteImages } from "@/lib/siteImages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOB_TYPES = [
  "Bathroom Renovation", "Bathroom Repair", "Blocked Drain", "Burst Pipe",
  "Civil Works", "Drainage", "Emergency Plumbing", "General Plumbing",
  "Hot Water System", "Leak Detection", "Pipe Repair", "Tap/Fixture Repair",
  "Toilet Repair", "Other",
];

const URGENCIES = ["Emergency", "Today", "This week", "Flexible"];

const Contact = () => {
  const [form, setForm] = useState({
    fullName: "", suburb: "", postcode: "", phone: "",
    jobType: "", description: "", urgency: "", email: "", notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { slots } = useSiteImages();

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/quote-requests`, form);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative">
        <div className="relative mx-auto max-w-page px-6 lg:px-10 pt-20 pb-16">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Contact</span>
          </nav>
        </div>
      </section>

      <section id="quote" className="pb-24">
        <div className="mx-auto max-w-page px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Request a Quote</div>
            <h1 className="font-display text-5xl md:text-7xl text-ink mb-6">Get in Touch</h1>
            <p className="text-muted text-lg leading-relaxed mb-10 max-w-md">
              Tell us about your project and we'll provide a detailed quote within 24 hours.
              Emergency services available around the clock.
            </p>
            {slots.contact && (
              <img src={slots.contact} alt="Styne Group" data-testid="contact-photo"
                className="w-full max-w-md aspect-[4/3] object-cover border border-line mb-10" />
            )}
            <div className="space-y-6">
              <a href={PHONE_HREF} className="flex items-start gap-4 group" data-testid="contact-phone">
                <div className="w-11 h-11 border border-line group-hover:border-gold flex items-center justify-center transition-colors">
                  <Phone size={16} className="text-gold" />
                </div>
                <div>
                  <div className="text-muted text-xs tracking-widerx uppercase mb-1">Call Us</div>
                  <div className="font-display text-2xl text-ink group-hover:text-gold transition-colors">{PHONE}</div>
                </div>
              </a>
              <a href={EMAIL_HREF} className="flex items-start gap-4 group" data-testid="contact-email">
                <div className="w-11 h-11 border border-line group-hover:border-gold flex items-center justify-center transition-colors">
                  <Mail size={16} className="text-gold" />
                </div>
                <div>
                  <div className="text-muted text-xs tracking-widerx uppercase mb-1">Email</div>
                  <div className="text-ink group-hover:text-gold transition-colors break-all">{EMAIL}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-line flex items-center justify-center">
                  <MapPin size={16} className="text-gold" />
                </div>
                <div>
                  <div className="text-muted text-xs tracking-widerx uppercase mb-1">Service Area</div>
                  <div className="text-ink">Melbourne & Yarra Valley</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-card border border-line p-8 md:p-10">
            <div className="font-display text-2xl text-gold tracking-wide mb-8">Fast Quote</div>

            {submitted ? (
              <div className="py-16 text-center" data-testid="contact-success">
                <CheckCircle2 size={48} className="text-gold mx-auto mb-5" />
                <h3 className="font-display text-3xl text-ink mb-3">Request Received</h3>
                <p className="text-muted max-w-md mx-auto">
                  Thanks — we've got your details. We'll be in touch within 24 hours.
                  For emergencies, call <a href={PHONE_HREF} className="text-gold">{PHONE}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="quote-form">
                <input
                  type="text"
                  required
                  value={form.fullName}
                  onChange={onChange("fullName")}
                  placeholder="Full Name *"
                  className="w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors"
                  data-testid="quote-fullName"
                />
                <div className="grid grid-cols-2 gap-5">
                  <input
                    type="text"
                    required
                    value={form.suburb}
                    onChange={onChange("suburb")}
                    placeholder="Suburb *"
                    className="w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors"
                    data-testid="quote-suburb"
                  />
                  <input
                    type="text"
                    required
                    value={form.postcode}
                    onChange={onChange("postcode")}
                    placeholder="Postcode *"
                    className="w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors"
                    data-testid="quote-postcode"
                  />
                </div>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="Phone Number *"
                  className="w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors"
                  data-testid="quote-phone"
                />
                <select
                  required
                  value={form.jobType}
                  onChange={onChange("jobType")}
                  className="w-full bg-transparent border-b border-line focus:border-gold text-ink py-3 outline-none transition-colors"
                  data-testid="quote-jobType"
                >
                  <option value="" className="bg-card text-muted">Select Job Type *</option>
                  {JOB_TYPES.map(j => (
                    <option key={j} value={j} className="bg-card text-ink">{j}</option>
                  ))}
                </select>
                <textarea
                  required
                  rows={3}
                  value={form.description}
                  onChange={onChange("description")}
                  placeholder="Describe your project or issue *"
                  className="w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors resize-none"
                  data-testid="quote-description"
                />
                <div>
                  <div className="text-muted text-xs tracking-widerx uppercase mb-3">Urgency *</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {URGENCIES.map(u => (
                      <button
                        type="button"
                        key={u}
                        onClick={() => setForm({...form, urgency: u})}
                        data-testid={`quote-urgency-${u.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`py-3 border text-xs tracking-widerx uppercase transition-colors ${
                          form.urgency === u
                            ? 'border-gold text-gold bg-gold/5'
                            : 'border-line text-ink hover:border-gold/40'
                        }`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pt-3">
                  <div className="text-muted text-xs tracking-widerx uppercase mb-3">Optional</div>
                  <input
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="Email Address"
                    className="w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors"
                    data-testid="quote-email"
                  />
                </div>
                <input
                  type="text"
                  value={form.notes}
                  onChange={onChange("notes")}
                  placeholder="Access notes (e.g., gate code, parking)"
                  className="w-full bg-transparent border-b border-line focus:border-gold text-ink placeholder:text-muted py-3 outline-none transition-colors"
                  data-testid="quote-notes"
                />
                {error && <div className="text-emergency text-sm">{error}</div>}
                <button
                  type="submit"
                  disabled={submitting || !form.urgency}
                  data-testid="quote-submit"
                  className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={16} /> {submitting ? "Sending..." : "Submit Request"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
